import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Card, CardContent, Dialog, Loading, Tab, Tabs, TextArea, TextInput, Toggle, } from '@guildeducationinc/recess';
import { Button } from '@mui/material';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { getIntegrationsNavLinks } from '@src/utilities/topNavHelper';
import _ from 'lodash';
import { DatePicker, EnumPicker, IntegrationConfigDirection, IntegrationConfigPicker, MessageType, Messages, useLocale, } from 'pe-core-frontend';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { GET_REPORT_TEMPLATE, SAVE_REPORT_TEMPLATE } from '../../../graphql/queries/reportTemplate';
import { ReportType, emptyReportTemplate, } from '../../../types';
import { ReportConfig } from '../ReportConfig';
import { ReportViewer } from '../ReportViewer';
export const ReportTemplate = () => {
    const { t } = useLocale();
    const navigate = useNavigate();
    const reportTemplateValidation = z.object({
        name: z
            .string()
            .min(1, { message: t('{field} is required', { values: { field: t('Name') } }) }),
    });
    const { id = '', clientId = '', type = '', } = useParams();
    const [reportTemplate, setReportTemplate] = useState({
        ...emptyReportTemplate,
        clientId: Number(clientId),
        type,
        settings: {
            ...emptyReportTemplate.settings,
            spec: {
                ...emptyReportTemplate.settings.spec,
                clientId: Number(clientId),
            },
        },
    });
    const [messages, setMessages] = useState([]);
    const [errors, setErrors] = useState({});
    const [saveReportTemplate] = useMutation(SAVE_REPORT_TEMPLATE);
    const { data, loading } = useQuery(GET_REPORT_TEMPLATE, {
        variables: { id: Number(id) },
        skip: isNaN(Number(id)),
    });
    const [showReportData, setShowReportData] = useState(false);
    const [processing, setProcessing] = useState(false);
    useEffect(() => {
        if (!loading && data) {
            setReportTemplate({ ...data.getReportTemplate });
        }
    }, [data, loading]);
    useEffect(() => {
        setErrors({});
    }, [reportTemplate]);
    const validateForm = () => {
        let success = true;
        const errors = {};
        const result = reportTemplateValidation.safeParse(reportTemplate);
        if (result.success === false) {
            success = false;
            result.error.issues.forEach(issue => {
                errors[issue.path[0]] = issue.message;
            });
        }
        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            success = false;
        }
        return success;
    };
    const handleSave = async () => {
        try {
            if (validateForm()) {
                setProcessing(true);
                const saved = await saveReportTemplate({
                    variables: {
                        input: {
                            ..._.omit(reportTemplate, [
                                'id',
                                'createdAt',
                                'updatedAt',
                                '__typename',
                                'lastRunAt',
                                'lastRunStatus',
                                'lastRunMessage',
                            ]),
                            id: reportTemplate.id ? reportTemplate.id : null,
                            integrationConfigId: reportTemplate.integrationConfigId
                                ? reportTemplate.integrationConfigId
                                : null,
                            cronSchedules: reportTemplate.cronSchedules ?? null,
                            startDate: reportTemplate.startDate ?? null,
                            daysApart: reportTemplate.daysApart ? reportTemplate.daysApart : null,
                            publicKey: reportTemplate.publicKey ?? '',
                            extension: reportTemplate.extension ?? '',
                            shortName: reportTemplate.shortName ?? '',
                            filenamePattern: reportTemplate.filenamePattern ?? '',
                        },
                    },
                });
                if (saved.data?.saveReportTemplate) {
                    setReportTemplate({
                        ...reportTemplate,
                        id: saved.data.saveReportTemplate.id,
                    });
                }
                // message
                setMessages([{ type: MessageType.SUCCESS, value: t('Report Template Saved') }]);
            }
        }
        catch (ex) {
            if (ex.message) {
                setMessages([{ type: MessageType.ERROR, value: ex.message }]);
            }
        }
        finally {
            setProcessing(false);
        }
    };
    const handleCloseReportData = () => {
        setShowReportData(false);
    };
    const handleReportTypeChange = async (value) => {
        //reset settings
        setReportTemplate({
            ...reportTemplate,
            type: value,
            settings: {
                ...emptyReportTemplate.settings,
                spec: {
                    ...emptyReportTemplate.settings.spec,
                    clientId: Number(clientId),
                },
                columns: [],
            },
        });
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Integrations'), navLinks: getIntegrationsNavLinks(t, Number(clientId), 'report_templates') }), _jsxs("div", { children: [_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx("h4", { className: 'text-3xl pb-3', children: t('Report Template') }), _jsx(Messages, { messages: messages, onClose: () => setMessages([]) }), _jsx(Loading, { label: 'Report Template', loaded: !loading && !processing, showChildrenWhileLoading: true, overlay: true, children: _jsxs(Tabs, { children: [_jsx(Tab, { title: t('General'), children: _jsxs("div", { className: 'flex flex-col gap-3 mb-3', children: [_jsx(EnumPicker, { isClearable: false, labelPrefix: 'reportType', label: t('Report Type'), enumType: ReportType, value: reportTemplate.type, onChange: value => handleReportTypeChange(value) }), _jsx(TextInput, { label: t('Name'), value: reportTemplate.name, onChange: event => setReportTemplate({ ...reportTemplate, name: event.target.value }), errors: errors.name }), _jsx(IntegrationConfigPicker, { label: t('Integration Config'), clientId: reportTemplate.clientId, direction: IntegrationConfigDirection.OUT, value: String(reportTemplate.integrationConfigId), onChange: x => setReportTemplate({
                                                                ...reportTemplate,
                                                                integrationConfigId: Number(x?.id ?? 0),
                                                            }) }), _jsx(TextInput, { label: t('Schedule To Run At'), helpText: t('Use cron syntax to create a schedule. Example: 0 0 * * * = midnight every day.'), value: reportTemplate.cronSchedules ?? '', onChange: event => setReportTemplate({ ...reportTemplate, cronSchedules: event.target.value }), errors: errors.cronSchedules }), _jsx(DatePicker, { label: t('Starts On'), value: reportTemplate.startDate || '', onChange: startDate => setReportTemplate(prev => ({ ...prev, startDate })), errors: errors.startDate }), _jsx(TextInput, { label: t('Days Apart'), value: reportTemplate.daysApart ? String(reportTemplate.daysApart) : '', onChange: event => setReportTemplate({
                                                                ...reportTemplate,
                                                                daysApart: Number(event.target.value),
                                                            }), errors: errors.daysApart }), _jsx(Toggle, { label: t('Is Active?'), active: reportTemplate.isActive, onToggle: value => setReportTemplate({ ...reportTemplate, isActive: value }) }), _jsx(Toggle, { label: t('Show in Dashboard?'), active: reportTemplate.showInDashboard, onToggle: value => setReportTemplate({ ...reportTemplate, showInDashboard: value }) }), reportTemplate.type && (_jsx(_Fragment, { children: _jsx(ReportConfig, { reportTemplate: reportTemplate, onChange: reportTemplate => setReportTemplate(reportTemplate) }) })), _jsxs("div", { className: 'flex flexJustify--space_between mt-4', children: [_jsxs("div", { className: 'flex gap-2', children: [_jsx(Button, { variant: 'contained', onClick: () => handleSave(), children: t('Save') }), _jsx(Button, { variant: 'contained', onClick: () => setShowReportData(true), children: t('Test') })] }), _jsx(Button, { onClick: () => navigate(`/admin/clients/${clientId}/integrations/report-templates`), variant: 'outlined', children: t('Cancel') })] })] }) }), _jsx(Tab, { title: t('Encryption'), children: _jsxs("div", { className: 'flex flex-col gap-3 mb-3', children: [_jsx(TextArea, { fullWidth: true, autosize: false, label: t('Public Key'), value: reportTemplate.publicKey, onChange: event => setReportTemplate({
                                                                ...reportTemplate,
                                                                publicKey: event.currentTarget.value,
                                                            }), errors: errors.publicKey }), _jsx(TextInput, { label: t('Extension'), helpText: t('Example: .pgp or .gpg'), value: reportTemplate.extension, onChange: event => setReportTemplate({ ...reportTemplate, extension: event.target.value }), errors: errors.extension }), _jsx(TextInput, { label: t('Short Name'), value: reportTemplate.shortName, onChange: event => setReportTemplate({ ...reportTemplate, shortName: event.target.value }), errors: errors.shortName }), _jsx(TextInput, { label: t('Filename Pattern'), value: reportTemplate.filenamePattern, onChange: event => setReportTemplate({
                                                                ...reportTemplate,
                                                                filenamePattern: event.target.value,
                                                            }), errors: errors.filenamePattern })] }) })] }) })] }) }), _jsx(Dialog, { size: 'xl', forceFullScreen: true, isOpen: showReportData, onClose: handleCloseReportData, children: showReportData && _jsx(ReportViewer, { reportTemplate: reportTemplate }) })] })] }));
};
